import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Rocket, CalendarDays, Check, Sofa, PaintRoller, Grid3X3, Refrigerator,
  Bath, PenTool, Building2, Lamp, Globe, Store, Smartphone, QrCode,
  Presentation, ShoppingBag, Handshake, Users, MapPin, ShieldCheck, Code2,
  Building, GraduationCap, Headset, Workflow, Sparkles, ArrowUpRight,
} from "lucide-react";
import { CALENDLY_DEMO_URL } from "@/constants/links";

import Navbar from "@/components/home/Navbar";
import Footer from "@/components/home/Footer";
import FinalCTA from "@/components/home/FinalCTA";
import HeroIntro from "@/components/home/HeroIntro";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

import heroBg from "@/assets/providedassets/solutionfontpage.jpeg";

import furnitureImg from "@/assets/providedassets/furniture2.jpeg";
import paintImg from "@/assets/providedassets/paint2.jpeg";
import tileImg from "@/assets/providedassets/Tile & Flooring.jpg";
import kitchenImg from "@/assets/providedassets/Kitchen & Modular Interiors.jpg";
import bathroomImg from "@/assets/providedassets/bathroom.jpg";
import interiorImg from "@/assets/providedassets/interior Designers.jpg";
import realEstateImg from "@/assets/providedassets/Real Estate & Property Developers.jpg";
import decorImg from "@/assets/providedassets/homedecor2.jpg";

const INDUSTRIES = [
  {
    icon: Sofa,
    img: furnitureImg,
    name: "Furniture Retail",
    tagline: "Turn browsers into buyers",
    body: "Customers can place sofas, beds, wardrobes, dining tables, chairs, and décor into their own rooms before purchasing.",
    benefits: [
      "Increase conversion rates",
      "Reduce product returns",
      "Help customers choose the right products",
      "Upsell matching furniture collections",
      "Improve online and in-store shopping experiences",
    ],
  },
  {
    icon: PaintRoller,
    img: paintImg,
    name: "Paint & Wall Finishes",
    tagline: "Help customers choose the perfect color",
    body: "Allow customers to preview paint colors on their own walls under realistic lighting conditions.",
    benefits: [
      "Reduce color uncertainty",
      "Increase paint sales",
      "Simplify decision-making",
      "Promote complementary products",
    ],
  },
  {
    icon: Grid3X3,
    img: tileImg,
    name: "Tile & Flooring",
    tagline: "Eliminate guesswork before installation",
    body: "Customers can compare flooring materials, wall tiles, textures, and finishes in their own spaces.",
    benefits: [
      "Faster purchase decisions",
      "Better product visualization",
      "Reduced showroom dependency",
      "Increased confidence for high-value purchases",
    ],
  },
  {
    icon: Refrigerator,
    img: kitchenImg,
    name: "Kitchen & Modular Interiors",
    tagline: "Design kitchens with confidence",
    body: "Enable customers to visualize cabinets, countertops, backsplashes, appliances, and layouts before installation.",
    benefits: [
      "Accelerate project approvals",
      "Improve customer engagement",
      "Reduce revision requests",
      "Increase premium product adoption",
    ],
  },
  {
    icon: Bath,
    img: bathroomImg,
    name: "Bathroom Solutions",
    tagline: "Visualize complete bathroom transformations",
    body: "Show customers how sanitaryware, vanities, mirrors, tiles, and accessories will look together.",
    benefits: [
      "Improve package sales",
      "Cross-sell accessories",
      "Increase customer satisfaction",
    ],
  },
  {
    icon: PenTool,
    img: interiorImg,
    name: "Interior Designers",
    tagline: "Create client concepts in minutes",
    body: "Generate multiple design concepts quickly without spending hours on manual rendering.",
    benefits: [
      "Save design time",
      "Present multiple ideas instantly",
      "Improve client collaboration",
      "Deliver projects faster",
    ],
  },
  {
    icon: Building2,
    img: realEstateImg,
    name: "Real Estate & Property Developers",
    tagline: "Sell properties before they are built",
    body: "Show furnished apartments, model homes, and commercial spaces using AI-powered virtual staging.",
    benefits: [
      "Improve project marketing",
      "Generate more qualified leads",
      "Increase buyer engagement",
      "Showcase multiple interior styles",
    ],
  },
  {
    icon: Lamp,
    img: decorImg,
    name: "Home Décor & Lifestyle Brands",
    tagline: "Inspire customers with complete room designs",
    body: "Help customers visualize décor products such as lighting, rugs, curtains, artwork, and accessories in realistic room settings.",
    benefits: [
      "Encourage bundle purchases",
      "Increase average order value",
      "Create immersive shopping experiences",
    ],
  },
];

const CAPABILITIES = [
  {
    title: "Renders your real catalog",
    body: "Every visualization uses your actual product photos and SKUs — never generic or invented furniture.",
  },
  {
    title: "Room-aware placement",
    body: "Scale, perspective, and lighting are automatically matched to the uploaded room.",
  },
  {
    title: "Consistent results",
    body: "The same product renders the same way every time, so customers get a reliable preview.",
  },
  {
    title: "Built for scale",
    body: "Generate visualizations in bulk or through the API — not just one image at a time.",
  },
  {
    title: "End-to-end commerce tools",
    body: "QR codes, storefront widgets, sales-team tools, and analytics built around every visualization.",
  },
  {
    title: "Clear commercial rights",
    body: "Licensing designed for retail and marketing use, backed by our team.",
  },
];

const ENTERPRISE_FEATURES = [
  { icon: Building, label: "Multi-brand management" },
  { icon: MapPin, label: "Multi-location deployment" },
  { icon: ShieldCheck, label: "Role-based user access" },
  { icon: Code2, label: "API integrations" },
  { icon: Users, label: "White-label platform" },
  { icon: GraduationCap, label: "Dedicated onboarding" },
  { icon: Headset, label: "Priority support" },
  { icon: Workflow, label: "Custom AI workflows" },
];

const CHANNELS = [
  { icon: Globe, label: "E-commerce websites" },
  { icon: Store, label: "Retail showrooms" },
  { icon: Smartphone, label: "Mobile devices" },
  { icon: QrCode, label: "QR codes" },
  { icon: Presentation, label: "Sales presentations" },
  { icon: ShoppingBag, label: "Trade exhibitions" },
  { icon: Handshake, label: "Dealer and distributor networks" },
];

export default function SolutionsPage() {
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-neutral-50 pb-16 pt-28 md:pb-20 md:pt-36">
          {/* Decorative right-side image panel */}
          <div className="pointer-events-none absolute right-0 top-0 h-full w-[45%] hidden lg:block" aria-hidden="true">
            <img
              src={heroBg}
              alt=""
              className="h-full w-full object-cover saturate-[1.35] contrast-[1.08] brightness-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-50/80 via-neutral-50/10 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-neutral-50/10" />
          </div>

          <div className="relative mx-auto max-w-6xl px-4 md:px-8">
            <div className="max-w-xl">
            <HeroIntro eyebrow="Solutions" heading="AI solutions for every business that sells physical products">
              <p className="mt-5 max-w-lg text-lg leading-relaxed text-neutral-500 md:text-xl">
                Whether you're selling furniture, paint, flooring, kitchens, or
                commercial interiors, NEIV helps customers visualize products
                before they buy — improving confidence, accelerating decisions,
                and increasing sales.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/signup"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 sm:w-auto"
                >
                  <Rocket className="h-4 w-4" />
                  Start Free
                </Link>
                <a
                  href={CALENDLY_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 px-8 py-3.5 text-base font-semibold text-neutral-800 transition-all hover:border-neutral-900 sm:w-auto"
                >
                  <CalendarDays className="h-4 w-4" />
                  Book a Demo
                </a>
              </div>
              <div className="mt-10 max-w-sm rounded-2xl border border-neutral-200 bg-white/80 px-6 py-5 shadow-sm backdrop-blur-sm">
                <p className="text-sm text-neutral-400">Every customer asks the same question:</p>
                <p className="mt-1 text-xl font-semibold text-neutral-800">"How will this look in my space?"</p>
                <p className="mt-1 text-sm text-neutral-400">NEIV answers that with realistic AI visualization.</p>
              </div>
            </HeroIntro>
            </div>
          </div>
        </section>

        {/* What makes NEIV different */}
        <section className="bg-white py-10">
          <div className="mx-auto max-w-6xl px-4 md:px-8">
            <button
              type="button"
              onClick={() => setCapabilitiesOpen(true)}
              className="group mx-auto flex w-full max-w-2xl items-center justify-between gap-4 rounded-2xl border border-neutral-200 bg-white p-6 text-left shadow-sm shadow-neutral-900/[0.03] transition-shadow hover:shadow-lg hover:shadow-neutral-900/[0.06] sm:p-8"
            >
              <div className="flex items-center gap-4">
                <div className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-neutral-900">What makes NEIV different</h3>
                  <p className="mt-1 text-sm text-neutral-500">See what powers every visualization</p>
                </div>
              </div>
              <ArrowUpRight className="h-5 w-5 flex-shrink-0 text-primary transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
          </div>

          <Dialog open={capabilitiesOpen} onOpenChange={setCapabilitiesOpen}>
            <DialogContent className="max-w-2xl gap-0 p-0">
              <DialogHeader className="p-6 pb-4 sm:p-8 sm:pb-4">
                <p className="text-sm font-bold uppercase tracking-widest text-primary">What makes NEIV different</p>
                <DialogTitle className="mt-1 text-2xl font-bold text-neutral-900">
                  Built to sell your actual products
                </DialogTitle>
                <DialogDescription className="mt-1 text-neutral-500">
                  Every visualization on NEIV is grounded in your real catalog and built for commerce.
                </DialogDescription>
              </DialogHeader>

              <div className="max-h-[70vh] overflow-y-auto px-6 pb-6 sm:px-8 sm:pb-8">
                <div className="space-y-5">
                  {CAPABILITIES.map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Check className="h-3.5 w-3.5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-neutral-900">{item.title}</p>
                        <p className="mt-0.5 text-sm leading-relaxed text-neutral-500">{item.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </section>

        {/* Industry solutions */}
        <section id="industries" className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Industries</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Solutions for your industry
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {INDUSTRIES.map((industry) => {
                const Icon = industry.icon;
                return (
                  <div
                    key={industry.name}
                    className="group grid overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm shadow-neutral-900/[0.03] transition-shadow hover:shadow-md sm:grid-cols-5"
                  >
                    <div className="relative min-h-[260px] overflow-hidden sm:col-span-2 sm:min-h-0">
                      <img
                        src={industry.img}
                        alt={industry.name}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/60 via-neutral-950/10 to-transparent" />
                      <div className="absolute bottom-4 left-6 right-4 flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/90 text-primary backdrop-blur">
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="text-xl font-bold leading-tight text-white">{industry.name}</h3>
                      </div>
                    </div>
                    <div className="p-8 sm:col-span-3">
                      <p className="font-medium text-primary">{industry.tagline}</p>
                      <p className="mt-3 leading-relaxed text-neutral-500">{industry.body}</p>
                      <p className="mt-5 text-xs font-bold uppercase tracking-widest text-neutral-400">
                        Business Benefits
                      </p>
                      <ul className="mt-3 space-y-2">
                        {industry.benefits.map((benefit) => (
                          <li key={benefit} className="flex items-start gap-2.5 text-neutral-700">
                            <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                            <span className="text-sm font-medium">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Enterprise solutions */}
        <section className="bg-neutral-900 py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">Enterprise Solutions</p>
              <h2 className="mt-3 text-3xl font-bold text-white md:text-4xl">
                Built for large organizations
              </h2>
              <p className="mt-4 text-lg text-white/60">
                NEIV scales with businesses operating across multiple
                locations, brands, and teams.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {ENTERPRISE_FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.label}
                    className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center"
                  >
                    <Icon className="mx-auto h-6 w-6 text-primary" />
                    <p className="mt-3 text-sm font-semibold text-white">{feature.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Sales channels */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-5xl px-4 text-center md:px-8">
            <p className="text-sm font-bold uppercase tracking-widest text-primary">Channels</p>
            <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
              Success across every sales channel
            </h2>
            <p className="mt-4 text-lg text-neutral-500">
              Use NEIV wherever your customers shop.
            </p>
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              {CHANNELS.map((channel) => {
                const Icon = channel.icon;
                return (
                  <div
                    key={channel.label}
                    className="flex items-center gap-3 rounded-full border border-neutral-200 bg-white px-6 py-3 shadow-sm shadow-neutral-900/[0.03]"
                  >
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="text-sm font-semibold text-neutral-800">{channel.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <FinalCTA
          title="Ready to transform customer experiences?"
          subtitle="Whether you're a growing retailer or a global enterprise, NEIV helps customers make better purchasing decisions through AI-powered visualization."
          primaryLabel="Start Free"
          secondaryLabel="Book a Demo"
        />
      </main>
      <Footer />
    </div>
  );
}
